import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useFocusTrap } from '@/shared/hooks/useFocusTrap';
import { Button, type ButtonSize, type ButtonVariant } from '@/shared/components/Button';
import { cn } from '@/shared/helpers/cn';
import styles from './Popover.module.scss';

export interface PopoverProps {
  /** Nome do gatilho e do painel — e o que o leitor de tela anuncia nos dois. */
  label: string;
  /** Rotulo visivel do gatilho. Sem ele, o botao e so o icone. */
  triggerLabel?: ReactNode;
  /** Icone do gatilho. Decorativo — quem nomeia a acao e o `label`. */
  icon?: ReactNode;
  /** Titulo visivel no topo do painel. Sem ele, o painel usa o `label`. */
  title?: string;
  /** Lado pelo qual o painel se alinha ao gatilho. */
  align?: 'start' | 'end';
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: ReactNode;
  /** Classe do gatilho — para o botao acompanhar os vizinhos da barra. */
  triggerClassName?: string;
  /** Classe da raiz, que ancora o painel ao gatilho. */
  className?: string;
}

/**
 * Painel ancorado a um botao.
 *
 * Para o punhado de controles que **configura** uma tela sem filtrar o que ela
 * mostra — quantos itens por pagina, como a grade se desenha. Na barra, cada um
 * deles gasta uma coluna inteira e compete com o filtro que o usuario de fato
 * usa; aqui eles ficam a um clique, agrupados e nomeados.
 *
 * **Nao e menu.** O conteudo sao campos de formulario, e `role="menu"` obriga
 * cada filho a ser um `menuitem` navegavel por setas — leitor de tela nenhum
 * anuncia um `select` dentro disso. O painel e um `dialog` nao modal: `Escape`
 * fecha, clique fora fecha, e o foco volta para o gatilho.
 */
export function Popover({
  label,
  triggerLabel,
  icon,
  title,
  align = 'end',
  variant = 'secondary',
  size = 'md',
  children,
  triggerClassName,
  className,
}: PopoverProps) {
  const painelId = useId();
  const tituloId = useId();
  const [aberto, setAberto] = useState(false);
  const raizRef = useRef<HTMLDivElement>(null);
  const painelRef = useRef<HTMLDivElement>(null);

  const fechar = useCallback(() => {
    setAberto(false);
  }, []);

  // O foco entra no painel ao abrir e volta ao gatilho ao fechar — sem isso,
  // quem navega por teclado abre o painel e continua tabulando na barra.
  useFocusTrap(painelRef, aberto, fechar);

  useEffect(() => {
    if (!aberto) return;

    const aoApontar = (evento: MouseEvent): void => {
      if (raizRef.current?.contains(evento.target as Node)) return;
      setAberto(false);
    };

    document.addEventListener('mousedown', aoApontar);
    return () => document.removeEventListener('mousedown', aoApontar);
  }, [aberto]);

  return (
    <div ref={raizRef} className={cn(styles.root, className)}>
      <Button
        variant={variant}
        size={size}
        icon={icon}
        className={triggerClassName}
        aria-label={triggerLabel ? undefined : label}
        aria-haspopup="dialog"
        aria-expanded={aberto}
        aria-controls={aberto ? painelId : undefined}
        onClick={() => setAberto((atual) => !atual)}
      >
        {triggerLabel}
      </Button>

      {aberto ? (
        <div
          ref={painelRef}
          id={painelId}
          role="dialog"
          aria-labelledby={tituloId}
          tabIndex={-1}
          className={cn(styles.painel, styles[align])}
        >
          <h2 id={tituloId} className={styles.titulo}>
            {title ?? label}
          </h2>
          <div className={styles.corpo}>{children}</div>
        </div>
      ) : null}
    </div>
  );
}
